import Box, { BoxProps } from "@mui/material/Box";
import { SxProps, Theme } from "@mui/material/styles";
import {
  AllCommunityModule,
  iconSetMaterial,
  ThemeDefaultParams,
  themeQuartz,
} from "ag-grid-community";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { useMemo } from "react";

import { dataGridColorParams } from "../../theme/data-grid-colors";

import { formatCurrency, formatPercent, NULL_DISPLAY } from "./formatters";

import type { DataTypeDefinition } from "ag-grid-community";

const BUILT_IN_DATA_TYPE_DEFINITIONS: Record<string, DataTypeDefinition> = {
  currency: {
    baseDataType: "number",
    extendsDataType: "number",
    valueFormatter: (params) => {
      if (params.value == null) return NULL_DISPLAY;
      return formatCurrency(params.value as number);
    },
  },
  percent: {
    baseDataType: "number",
    extendsDataType: "number",
    valueFormatter: (params) => {
      if (params.value == null) return NULL_DISPLAY;
      return formatPercent(params.value as number);
    },
  },
};

export interface GridParams<TData = unknown> extends AgGridReactProps<TData> {
  themeParams?: Partial<ThemeDefaultParams>;
  sx?: SxProps<Theme>;
  containerProps?: Omit<BoxProps, "sx"> & {
    [key: `data-${string}`]: string | undefined;
  };
}

/**
 * A reusable data grid component built on top of `ag-grid-react` and styled with Material-UI.
 *
 * Accepts all props from `AgGridReact` and allows customization of the grid theme and container styles
 * using Material-UI's `sx` prop and `themeParams`.
 *
 * @returns {React.JSX.Element} The rendered data grid component.
 */

function DataGrid<TData = unknown>({
  sx,
  themeParams,
  defaultColDef,
  containerProps,
  dataTypeDefinitions: consumerDataTypeDefinitions,
  ...agGridProps
}: GridParams<TData>): React.JSX.Element {
  const theme = useMemo(
    () =>
      themeQuartz.withPart(iconSetMaterial).withParams({
        ...dataGridColorParams,
        browserColorScheme: "inherit",
        columnBorder: true,
        fontSize: 14,
        headerFontSize: 14,
        iconSize: 14,
        spacing: 4,
        borderRadius: 0, // Remove all border radius for seamless alignment with RatesGrid
        ...themeParams,
      }),
    [themeParams],
  );

  const computedDefaultColDef = useMemo(
    () => ({
      flex: 1,
      ...defaultColDef,
    }),
    [defaultColDef],
  );

  const dataTypeDefinitions = useMemo(
    () => ({
      ...BUILT_IN_DATA_TYPE_DEFINITIONS,
      ...consumerDataTypeDefinitions,
    }),

    [consumerDataTypeDefinitions],
  );

  return (
    <Box
      data-testid="ds-data-grid"
      {...containerProps}
      sx={{
        "& .ag-center-cols-container": {
          minHeight: "unset !important", // Override AG Grid's default 150px min-height
        },
        "& .ag-center-cols-viewport": {
          minHeight: "unset !important", // Override AG Grid's default 150px min-height
        },
        ...sx,
      }}
    >
      <AgGridReact
        theme={theme}
        modules={[AllCommunityModule]}
        defaultColDef={computedDefaultColDef}
        dataTypeDefinitions={dataTypeDefinitions}
        {...agGridProps}
      />
    </Box>
  );
}

export default DataGrid;
